/**
 * @fileoverview Sounds Component
 * 
 * A component that listens for sound triggers from the app store and plays
 * the appropriate sounds using the use-sound library. This component should be
 * mounted at the base of the VTC app to handle all sound playback.
 * 
 * @module vtc/components/Sounds
 */

import { useEffect } from 'react';
import useSound from 'use-sound';
import useAppStore from '../../stores/app';

import newSessionSound from './files/new-session.mp3';
import newMessageSound from './files/new-message.mp3';
import handRaisedSound from './files/hand-raised.wav';

/**
 * Sound Configuration
 * 
 * CENTRALIZED SOUND CONFIGURATION - Add all new sounds here!
 * 
 * To add a new sound, follow these steps:
 * 
 * 1. Import the sound file at the top of this file:
 *    import myNewSound from './files/my-new-sound.mp3';
 * 
 * 2. Add an entry to SOUND_CONFIG below:
 *    'my-sound-name': {
 *        file: myNewSound,
 *        options: { volume: 0.5 }
 *    }
 * 
 * 3. Add a useSound hook call in the Sounds component:
 *    const [playMySound] = useSound(SOUND_CONFIG['my-sound-name'].file, SOUND_CONFIG['my-sound-name'].options);
 * 
 * 4. Add the sound to the soundMap:
 *    'my-sound-name': playMySound,
 * 
 * 5. Add the play function to the useEffect dependencies:
 *    }, [soundToPlay, allowSounds, clearSound, playNewSession, playNewMessage, playMySound]);
 * 
 * That's it! You can now trigger the sound with:
 * useAppStore.getState().playSound('my-sound-name');
 */
const SOUND_CONFIG = {
    'new-session': {
        file: newSessionSound,
        options: { volume: 0.5 }
    },
    'new-message': {
        file: newMessageSound,
        options: { volume: 0.5 }
    },
    'hand-raised': {
        file: handRaisedSound,
        options: { volume: 0.5 }
    }
};

/**
 * Sounds Component
 * 
 * Monitors the app store for sound triggers and plays sounds accordingly.
 * Respects the user's sound preferences from the app store.
 * Supports custom options for each sound playback.
 * 
 * @component
 * @example
 * // Mount at the base of your VTC app
 * <Sounds />
 */
function Sounds() {
    // Subscribe to sound trigger from app store
    const soundToPlay = useAppStore((state) => state.soundToPlay);
    const clearSound = useAppStore((state) => state.clearSound);

    // Check if sounds are allowed
    const allowSounds = useAppStore((state) => state.allowSounds);

    // Load all sounds from config using useSound
    // Each sound is loaded as a separate hook call (hooks must be called unconditionally)
    const [playNewSession] = useSound(SOUND_CONFIG['new-session'].file, SOUND_CONFIG['new-session'].options);
    const [playNewMessage] = useSound(SOUND_CONFIG['new-message'].file, SOUND_CONFIG['new-message'].options);
    const [playHandRaised] = useSound(SOUND_CONFIG['hand-raised'].file, SOUND_CONFIG['hand-raised'].options);

    // Map sound names to their play functions
    // This map is automatically built from the loaded sounds
    const soundMap = {
        'new-session': playNewSession,
        'new-message': playNewMessage,
        'hand-raised': playHandRaised
    };

    // Effect to play sounds when triggered
    useEffect(() => {
        if (soundToPlay && allowSounds) {
            const { name, options = {} } = soundToPlay;
            const playFunction = soundMap[name];

            if (playFunction) {
                // Play the sound with custom options
                // The options object can include: volume, playbackRate, interrupt, onend, etc.
                playFunction(options);
            } else {
                console.warn(`Unknown sound: ${name}`);
            }

            // Clear the sound trigger after playing
            clearSound();
        }
    }, [soundToPlay, allowSounds, clearSound, playNewSession, playNewMessage]);

    // This component doesn't render anything
    return null;
}

export default Sounds;
